// Backend role_id values used by the Pantry User module.
export const USER_ROLE_IDS = {
  OWNER: 'owner',
  ADMIN: 'admin',
  GENERAL_MANAGER: 'general_manager',
  MARKETING_MANAGER: 'marketing_manager',
  MARKETING_AGENT: 'marketing_agent',
  SERVICE_MANAGER: 'service_manager',
  SERVICE_AGENT: 'service_agent',
  CUSTOM_ROLE: 'custom_role',
} as const

export type UserRoleId = (typeof USER_ROLE_IDS)[keyof typeof USER_ROLE_IDS]

// Display labels for each role id.
// Note: the legacy POS labels ("General Manager", "Online Store Manager/Agent")
// have been retired in favor of pantry-storefront-friendly labels
// ("Manager", "Marketing", "Staff"). The `service_*` labels are kept as-is so
// any legacy assignment still renders correctly in the list/drawer, even
// though they're no longer offered in the assignable dropdown.
export const USER_ROLE_NAMES: Record<string, string> = {
  [USER_ROLE_IDS.OWNER]: 'Owner',
  [USER_ROLE_IDS.ADMIN]: 'Admin',
  [USER_ROLE_IDS.GENERAL_MANAGER]: 'Manager',
  [USER_ROLE_IDS.MARKETING_MANAGER]: 'Marketing',
  [USER_ROLE_IDS.MARKETING_AGENT]: 'Staff',
  [USER_ROLE_IDS.SERVICE_MANAGER]: 'Service Manager',
  [USER_ROLE_IDS.SERVICE_AGENT]: 'Service Agent',
  [USER_ROLE_IDS.CUSTOM_ROLE]: 'Custom Role',
}

// Assignable roles in the invite / edit-user flows.
// Owner / Admin are excluded — those are derived from `is_owner` / `is_ga`,
// not assigned via role_id (Admin is now driven by the dedicated checkbox in
// the InviteUserDialog). Service Manager / Service Agent are excluded because
// they are a POS-only concept that doesn't apply to pantry storefront users
// (existing assignments still display in the list/drawer via USER_ROLE_NAMES).
// Custom Role is omitted from this base list and added separately via
// USER_ROLE_OPTIONS_WITH_CUSTOM, since it only appears in flows that include
// the permission-matrix UI.
export const USER_ROLE_OPTIONS: Array<{ value: string; label: string }> = [
  {
    value: USER_ROLE_IDS.GENERAL_MANAGER,
    label: USER_ROLE_NAMES[USER_ROLE_IDS.GENERAL_MANAGER],
  },
  {
    value: USER_ROLE_IDS.MARKETING_MANAGER,
    label: USER_ROLE_NAMES[USER_ROLE_IDS.MARKETING_MANAGER],
  },
  {
    value: USER_ROLE_IDS.MARKETING_AGENT,
    label: USER_ROLE_NAMES[USER_ROLE_IDS.MARKETING_AGENT],
  },
]

// Same as USER_ROLE_OPTIONS plus "Custom Role" — for the InviteUserDialog
// where the user can override the role's default permissions via the
// permission-matrix UI.
export const USER_ROLE_OPTIONS_WITH_CUSTOM: Array<{
  value: string
  label: string
}> = [
  ...USER_ROLE_OPTIONS,
  {
    value: USER_ROLE_IDS.CUSTOM_ROLE,
    label: USER_ROLE_NAMES[USER_ROLE_IDS.CUSTOM_ROLE],
  },
]
